<template>
  <div
    class="flex justify-start flex-1 w-full rounded-b-lg h-52 gap-y-2 bg-eblack-50/70"
  >
    <ol class="relative w-full p-2 overflow-x-auto overflow-y-auto max-h-52">
      <li
        class="relative pl-6 mb-4 border-gray-500 ms-3 border-s"
        v-for="tour in bookingItems"
        :key="tour.id"
      >
        <span
          class="absolute flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full -start-3"
        >
          <svg
            class="w-2.5 h-2.5 text-blue-800"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              d="M20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4ZM0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm5-8h10a1 1 0 0 1 0 2H5a1 1 0 0 1 0-2Z"
            />
          </svg>
        </span>
        <h3 class="mb-1 font-semibold text-gray-900 text-md">
          {{ tour.service.name }}
        </h3>
        <p class="block mb-1 text-xs font-normal leading-none text-gray-500">
          {{ t("itinerary.info.provider", "Proveedor") }}:
          {{ tour.service.provider?.name || "N/A" }}
        </p>
        <!-- Date & Time Display -->
        <template
          v-if="tour.service_slot.start_date || tour.service_slot.end_date"
        >
          <p class="block mb-1 text-xs font-normal leading-none text-gray-500">
            {{ t("itinerary.info.date", "Fecha") }}:
            {{
              formatServiceDate(
                tour.service_slot.start_date || tour.service_slot.end_date,
              )
            }}
          </p>
          <p class="block mb-2 text-xs font-normal leading-none text-gray-500">
            {{ t("itinerary.info.time", "Horario") }}:
            {{
              formatServiceTime(
                tour.service_slot.start_date,
                tour.service_slot.end_date,
              )
            }}
          </p>
        </template>
        <p class="text-xs font-normal text-gray-600">
          {{ tour.service.description }}
        </p>

        <!-- Custom Field Values Display -->
        <div
          v-if="hasCustomFieldValues(tour)"
          class="pt-2 mt-2 border-t border-gray-200"
        >
          <p class="mb-1 text-xs font-medium text-gray-700">
            {{ getServiceTypeName(tour) }}
          </p>
          <div class="space-y-1">
            <div
              v-for="(value, key) in getDisplayableCustomFields(tour)"
              :key="key"
              class="text-xs text-gray-600"
            >
              <span class="font-medium">{{ getFieldLabel(tour, key) }}:</span>
              <span class="ml-1">{{ formatFieldValue(value) }}</span>
            </div>
          </div>
        </div>

        <!-- Per-service notes -->
        <div v-if="tour.notes" class="mt-2 pt-2 border-t border-gray-200">
          <p class="text-xs font-medium text-gray-700 mb-1">
            {{ t("itinerary.info.notes", "Notas") }}
          </p>
          <p class="text-xs text-gray-600 whitespace-pre-wrap">{{ tour.notes }}</p>
        </div>
      </li>
    </ol>
  </div>
</template>

<script>
import {
  formatWallClockDateDisplay,
  formatWallClockTimeRangeDisplay,
} from "@/domains/shared/utils/date-utils";
import { useItineraryI18n } from "@/domains/itinerary/composables";

export default {
  props: ["itinerary"],
  setup() {
    const { t } = useItineraryI18n();
    return { t };
  },
  computed: {
    /**
     * Get booking items from either new (slots_itineraries) or old (itinerary_tour) format
     */
    bookingItems() {
      return (
        this.itinerary.slots_itineraries || this.itinerary.itinerary_tour || []
      );
    },
  },
  methods: {
    /**
     * Check if tour has custom field values to display
     */
    hasCustomFieldValues(tour) {
      const values = tour.custom_field_values;
      if (!values) return false;

      // Check if there are any non-empty values
      return Object.values(values).some(
        (v) => v !== null && v !== undefined && v !== "",
      );
    },

    /**
     * Get the service type name for display
     */
    getServiceTypeName(tour) {
      return (
        tour.service?.service_type?.name ||
        this.t("itinerary.info.additional_details", "Detalles adicionales")
      );
    },

    /**
     * Get custom fields that have values to display
     */
    getDisplayableCustomFields(tour) {
      const values = tour.custom_field_values || {};

      // Filter out empty values
      const displayable = {};
      for (const [key, value] of Object.entries(values)) {
        if (value !== null && value !== undefined && value !== "") {
          displayable[key] = value;
        }
      }

      return displayable;
    },

    /**
     * Get human-readable label for a field key
     * Uses schema if available, otherwise formats the key
     */
    getFieldLabel(tour, key) {
      const schema = tour.service?.service_type?.custom_fields_schema || [];
      const field = schema.find((f) => f.key === key);

      if (field && field.label) {
        return field.label;
      }

      // Fallback: format the key as a readable label
      // Convert snake_case or camelCase to Title Case
      return key
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },

    /**
     * Format an ISO datetime string as "DD/MM/YYYY"
     */
    formatServiceDate(isoString) {
      return formatWallClockDateDisplay(isoString);
    },

    /**
     * Format start/end ISO datetimes as "HH:MM AM/PM – HH:MM AM/PM"
     * Falls back to start-only or end-only if one is missing
     */
    formatServiceTime(startIso, endIso) {
      return formatWallClockTimeRangeDisplay(startIso, endIso);
    },

    /**
     * Format field value for display
     */
    formatFieldValue(value) {
      if (typeof value === "boolean") {
        return value
          ? this.t("itinerary.info.yes", "Sí")
          : this.t("itinerary.info.no", "No");
      }

      if (value instanceof Date) {
        return value.toLocaleDateString();
      }

      return String(value);
    },
  },
};
</script>

<style lang="scss" scoped></style>
